import { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';

import {
  ATTACHMENTS_PLACEMENTS,
  BUTTON_TYPES,
} from '@savgroup-front-common/constants';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import { DocumentData } from '@savgroup-front-common/core/formatters/getDocumentNameTranslated';
import { SUPPORTED_ICON_POSITIONS } from '@savgroup-front-common/core/helpers';
import useDownloadFileDocument from '@savgroup-front-common/core/hooks/useDownloadFileDocument';
import { DownloadIcon } from '@savgroup-front-common/core/protons/icons';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import { getDocumentInfo } from './FileDocument.helpers';
import {
  $DocumentButton,
  $DocumentContent,
  $DocumentIcon,
  $DocumentName,
  $DocumentNameDescription,
  $DocumentNameTitle,
  $DocumentWrapper,
} from './FileDocument.styles';
import messages from './messages';

const FileDocument: FunctionComponent<{
  document: DocumentData;
}> = ({ document }) => {
  const theme = useTheme();
  const {
    label,
    description,
    icon,
    url,
    fileId,
    moduleDefinitionId,
    moduleInstanceId,
    downloadType,
    date,
  } = getDocumentInfo({
    document,
  });

  const { handleActionDownloadDocument, handleDownloadDocumentByUrl } =
    useDownloadFileDocument();

  const shouldDisableDownload =
    downloadType === ATTACHMENTS_PLACEMENTS.CONTROL_ATTACHMENTS && !url;

  const handleDownload = () => {
    if (downloadType === ATTACHMENTS_PLACEMENTS.CONTROL_ATTACHMENTS) {
      handleDownloadDocumentByUrl({
        documentUrl: url,
        fileName: safeFormattedIntlString(label),
      });

      return;
    }

    handleActionDownloadDocument({
      downloadType,
      tokenId: url?.split('token=')[1],
      fileId,
      moduleDefinitionId,
      moduleInstanceId,
      fileName: safeFormattedIntlString(label),
      carrierLabelId: document.carrierLabelId,
    });
  };

  return (
    <$DocumentWrapper>
      <$DocumentContent>
        <$DocumentIcon>
          <MyAccountIcon
            icon={icon as ICONS_TYPE}
            color={theme.colors.paragraphTextColor}
          />
        </$DocumentIcon>
        <$DocumentName>
          <$DocumentNameTitle>
            <SafeFormattedMessageWithoutSpread message={label ?? ''} />
          </$DocumentNameTitle>
          <$DocumentNameDescription>
            <SafeFormattedMessageWithoutSpread
              message={description}
              values={{ date }}
            />
          </$DocumentNameDescription>
        </$DocumentName>
      </$DocumentContent>
      <$DocumentButton
        icon={<DownloadIcon />}
        position={SUPPORTED_ICON_POSITIONS.LEFT}
        type={BUTTON_TYPES.BUTTON}
        small
        tertiary
        disabled={shouldDisableDownload}
        ariaLabel={messages.downloadDocument}
        onClick={handleDownload}
      >
        <SafeFormattedMessageWithoutSpread
          message={messages.downloadDocument}
        />
      </$DocumentButton>
    </$DocumentWrapper>
  );
};

export default FileDocument;
